import React, { useState, useEffect, useContext } from "react";
import { format, parseISO, isPast, isToday } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Grid, Divider,
  IconButton, Avatar, CircularProgress, Tooltip,
  Select, MenuItem, FormControl, InputLabel,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import ReplyIcon from "@material-ui/icons/Reply";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  section: { marginBottom: 16 },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: 8,
    marginTop: 16,
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  fieldLabel: {
    fontSize: "0.75rem",
    color: "#888",
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: "0.95rem",
    color: "#333",
    marginBottom: 8,
  },
  commentBox: {
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    background: "#fafafa",
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4,
    justifyContent: "space-between",
  },
  commentAuthor: { fontWeight: 600, fontSize: "0.85rem", marginLeft: 8 },
  commentDate: { fontSize: "0.75rem", color: "#999", marginLeft: 8 },
  commentText: { fontSize: "0.9rem", marginTop: 4, whiteSpace: "pre-wrap" },
  replyBox: {
    border: "1px solid #e8e8e8",
    borderRadius: 6,
    padding: "8px 10px",
    marginTop: 8,
    marginLeft: 32,
    background: "#f5f5f5",
  },
  replyInput: { marginTop: 8, marginLeft: 32 },
  inputRow: { display: "flex", alignItems: "flex-end", gap: 8, marginTop: 12 },
  noteInput: {
    "& .MuiOutlinedInput-root": { alignItems: "flex-start" },
  },
  expiredText: { color: theme.palette.error.main, fontWeight: 700 },
}));

const CRM_STATUS = ["Não iniciado", "Contato", "Em andamento", "Proposta", "Follow up", "Sucesso", "Perdido"];

const CRM_STATUS_COLORS = {
  "Não iniciado": { background: '#eceff1', color: '#546e7a' },
  "Contato": { background: '#e3f2fd', color: '#1565c0' },
  "Em andamento": { background: '#fff8e1', color: '#f9a825' },
  "Proposta": { background: '#ede7f6', color: '#5e35b1' },
  "Follow up": { background: '#e0f2f1', color: '#00796b' },
  "Sucesso": { background: '#e8f5e9', color: '#2e7d32' },
  "Perdido": { background: '#ffebee', color: '#c62828' },
};

const StatusBadge = ({ status }) => (
  <span
    style={{
      ...(CRM_STATUS_COLORS[status] || CRM_STATUS_COLORS["Não iniciado"]),
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 10,
      display: 'inline-block',
    }}
  >
    {status}
  </span>
);

const MOTIVO_PERDA = [
  "Preço acima da percepção de valor",
  "Falta de entendimento da dor real do cliente",
  "Proposta genérica, pouco aderente ao cenário do cliente",
  "Concorrente com melhor relacionamento interno",
  "Concorrente com proposta mais simples de aprovar",
  "Ausência de urgência clara para fechar",
  "Ausência de retorno efetivo",
  "Cliente sem orçamento disponível ou aprovado",
  "Decisor real não envolvido na negociação",
  "Processo de compra interno mal mapeado",
  "Falta de confiança na capacidade de entrega",
  "Pouca clareza sobre escopo, prazos e responsabilidades",
  "Proposta comercial complexa ou confusa",
  "Risco percebido alto pelo cliente",
  "Benefícios pouco quantificados financeiramente",
  "Falta de diferenciação frente aos concorrentes",
  "Demora no retorno, follow-up ou envio de materiais",
  "Negociação conduzida apenas por preço",
  "Objeções não tratadas adequadamente",
  "Mudança de prioridade interna do cliente",
  "Falta de patrocínio interno para defender a contratação",
  "Outros",
];

const Field = ({ label, value }) => {
  const classes = useStyles();
  if (!value) return null;
  return (
    <div>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <Typography className={classes.fieldValue}>{value}</Typography>
    </div>
  );
};

const today = format(new Date(), "yyyy-MM-dd");

const CrmCardModal = ({ open, onClose, ticket, onSaved, lanes = [] }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);

  // Campos do negócio (editáveis)
  const [crmStatus, setCrmStatus] = useState("");
  const [valorNegocio, setValorNegocio] = useState("");
  const [motivoPerda, setMotivoPerda] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [dateErrors, setDateErrors] = useState({});
  const [laneId, setLaneId] = useState("lane0");

  // A lane do ticket é derivada das suas tags — igual à lógica do board
  // (organizeLanes em Kanban/index.js): sem tag = lane0, senão a 1ª tag que
  // bater com uma coluna existente.
  const getTicketLaneId = (t) => {
    if (!t) return "lane0";
    const tagIds = (t.tags || []).map(tag => String(tag.id));
    const match = lanes.find(l => l.id !== "lane0" && tagIds.includes(l.id));
    return match ? match.id : "lane0";
  };

  useEffect(() => {
    if (!open || !ticket) return;
    setCrmStatus(ticket.crmStatus || "Não iniciado");
    setValorNegocio(ticket.valorVenda != null ? String(ticket.valorVenda) : "");
    setMotivoPerda(ticket.motivoPerda || "");
    setDataInicio(ticket.dataInicio ? ticket.dataInicio.substring(0, 10) : "");
    setDataFim(ticket.dataFim ? ticket.dataFim.substring(0, 10) : "");
    setDateErrors({});
    setLaneId(getTicketLaneId(ticket));
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticket]);

  const fetchNotes = async () => {
    if (!ticket) return;
    setLoadingNotes(true);
    try {
      const { data } = await api.get("/ticket-notes/list", {
        params: { ticketId: ticket.id, contactId: ticket.contactId }
      });
      setNotes(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const validateDates = (inicio, fim) => {
    const errs = {};
    if (fim && fim < today) errs.dataFim = "A data limite não pode ser no passado";
    if (inicio && fim && fim < inicio) errs.dataFim = "A data limite não pode ser anterior à data de início";
    return errs;
  };

  const handleDataInicioChange = (e) => {
    const val = e.target.value;
    setDataInicio(val);
    setDateErrors(prev => {
      const errs = { ...prev };
      if (dataFim && val && dataFim < val) errs.dataFim = "A data limite não pode ser anterior à data de início";
      else if (dataFim && dataFim >= today && (!val || dataFim >= val)) delete errs.dataFim;
      return errs;
    });
  };

  const handleDataFimChange = (e) => {
    const val = e.target.value;
    setDataFim(val);
    setDateErrors(prev => {
      const errs = { ...prev };
      if (!val) { delete errs.dataFim; return errs; }
      if (val < today) errs.dataFim = "A data limite não pode ser no passado";
      else if (dataInicio && val < dataInicio) errs.dataFim = "A data limite não pode ser anterior à data de início";
      else delete errs.dataFim;
      return errs;
    });
  };

  // Mesmo formatador de data usado na exibição do card (dd/MM/yyyy).
  const formatDateForComment = (d) => {
    if (!d) return "—";
    try { return format(parseISO(d.substring(0, 10) + "T12:00:00"), "dd/MM/yyyy"); }
    catch { return d; }
  };

  // Formata cada campo do jeito que deve aparecer no texto do comentário de
  // alteração — datas em dd/MM/yyyy, valor em R$, status como texto puro.
  const formatFieldForComment = (field, value) => {
    if (field === "dataInicio" || field === "dataFim") {
      return value ? formatDateForComment(value) : "não definida";
    }
    if (field === "valorVenda") {
      return value != null && value !== ""
        ? `R$ ${Number(value).toFixed(2)}`
        : "não definido";
    }
    return value || "não definido";
  };

  // Rótulo de cada campo exatamente como deve aparecer no comentário —
  // ex.: "Em 13/09/2026 10:20:11 A Data de Início foi alterado de ... para ...".
  const FIELD_LABELS = {
    crmStatus: "O Status",
    valorVenda: "O valor do negócio",
    dataInicio: "A Data de Início",
    dataFim: "A Data Limite",
  };

  // Compara o valor antigo com o novo de cada campo (exceto Coluna, que já
  // tem seu próprio aviso de transferência) e monta UM ÚNICO comentário
  // reunindo todas as alterações da vez, uma por linha, no formato pedido:
  // "Em dd/MM/yyyy HH:mm:ss <Campo> foi alterado de <antigo> para <novo>."
  const buildChangeNote = (oldValues, newValues) => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm:ss");
    const changeLines = [];

    Object.keys(FIELD_LABELS).forEach(field => {
      const oldRaw = oldValues[field];
      const newRaw = newValues[field];
      // Normaliza null/"" para comparar de forma equivalente
      const oldNorm = oldRaw ?? "";
      const newNorm = newRaw ?? "";
      if (String(oldNorm) === String(newNorm)) return;

      const label = FIELD_LABELS[field];
      const oldText = formatFieldForComment(field, oldRaw);
      const newText = formatFieldForComment(field, newRaw);
      changeLines.push(
        `Em ${now} ${label} foi alterado de ${oldText} para ${newText}.`
      );
    });

    return changeLines.length > 0 ? changeLines.join("\n") : null;
  };

  const handleSaveNegocio = async () => {
    const errs = validateDates(dataInicio, dataFim);
    if (Object.keys(errs).length > 0) {
      setDateErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const updatedData = {
        crmStatus,
        valorVenda: valorNegocio ? parseFloat(valorNegocio) : null,
        motivoPerda: crmStatus === "Perdido" ? motivoPerda : null,
        dataInicio: dataInicio || null,
        dataFim: dataFim || null,
      };

      const changeNote = buildChangeNote(
        {
          crmStatus: ticket.crmStatus,
          valorVenda: ticket.valorVenda,
          dataInicio: ticket.dataInicio ? ticket.dataInicio.substring(0, 10) : null,
          dataFim: ticket.dataFim ? ticket.dataFim.substring(0, 10) : null,
        },
        updatedData
      );

      await api.put(`/tickets/${ticket.id}`, updatedData);

      // Mudança de coluna: mesma API usada pelo drag-and-drop do board
      // (remove a tag/coluna atual e, se não for "Sem coluna", aplica a nova).
      // A Coluna fica de fora do comentário de alteração de campo, como pedido.
      let newTags = ticket.tags || [];
      const currentLaneId = getTicketLaneId(ticket);
      if (laneId !== currentLaneId) {
        await api.delete(`/ticket-tags/${ticket.id}`);
        if (laneId !== "lane0") {
          await api.put(`/ticket-tags/${ticket.id}/${laneId}`);
          const newLane = lanes.find(l => l.id === laneId);
          newTags = newLane ? [{ id: Number(laneId), name: newLane.title, color: newLane.color }] : [];
        } else {
          newTags = [];
        }
      }

      // Registra em Comentários, num único comentário, todos os campos que
      // mudaram nesta ação de salvar. Uma falha aqui não desfaz o save
      // (o negócio já foi atualizado com sucesso).
      if (changeNote) {
        try {
          await api.post("/ticket-notes", {
            note: changeNote,
            ticketId: ticket.id,
            contactId: ticket.contactId,
            parentId: null,
          });
          fetchNotes();
        } catch (noteErr) {
          toastError(noteErr);
        }
      }

      toast.success("Negócio atualizado!");
      onSaved && onSaved({ ...ticket, ...updatedData, tags: newTags });
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post("/ticket-notes", {
        note: newNote.trim(),
        ticketId: ticket.id,
        contactId: ticket.contactId,
        parentId: null,
      });
      setNewNote("");
      fetchNotes();
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddReply = async (parentId) => {
    if (!replyText.trim()) return;
    try {
      await api.post("/ticket-notes", {
        note: replyText.trim(),
        ticketId: ticket.id,
        contactId: ticket.contactId,
        parentId,
      });
      setReplyText("");
      setReplyingTo(null);
      fetchNotes();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/ticket-notes/${noteId}`);
      fetchNotes();
    } catch (err) {
      toastError(err);
    }
  };

  if (!ticket) return null;

  const contact = ticket.contact || {};
  const isExpired = dataFim && isPast(new Date(dataFim + "T23:59:59")) &&
    !["Sucesso", "Perdido"].includes(crmStatus);

  const formatDate = (d) => {
    if (!d) return "—";
    try { return format(parseISO(d.substring(0, 10) + "T12:00:00"), "dd/MM/yyyy"); }
    catch { return d; }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Detalhes do Card — Ticket #{ticket.id}</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>

          {/* ── COLUNA ESQUERDA: Contato ── */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Dados do Contato</Typography>
            <Field label="Nome" value={contact.name} />
            <Field label="WhatsApp" value={contact.number} />
            <Field label="Telefone" value={contact.telefoneTel} />
            <Field label="E-mail" value={contact.email} />
            <Field label="Data de Nascimento" value={contact.birthDate ? formatDate(contact.birthDate) : null} />
            <Field label="CEP" value={contact.cep} />
            <Field label="Endereço" value={contact.endereco} />
            <Field label="Cidade" value={contact.cidade ? `${contact.cidade}${contact.estado ? " / " + contact.estado : ""}` : null} />
            <Field label="Razão Social" value={contact.razaoSocial} />
            <Field label="CNPJ" value={contact.cnpj} />
            <Field label="Segmento" value={contact.segmento} />
            <Field label="Atendente" value={ticket.user?.name} />
            <Field label="Setor" value={ticket.queue?.name} />
            {contact.observacoes && (
              <>
                <Typography className={classes.fieldLabel}>Observações do Contato</Typography>
                <Typography className={classes.fieldValue} style={{ whiteSpace: "pre-wrap" }}>
                  {contact.observacoes}
                </Typography>
              </>
            )}
          </Grid>

          {/* ── COLUNA DIREITA: Negócio ── */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Dados do Negócio</Typography>

            <TextField
              label="Valor do Negócio (R$)"
              type="number"
              value={valorNegocio}
              onChange={e => setValorNegocio(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              style={{ marginBottom: 12 }}
            />

            <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
              <InputLabel>Status *</InputLabel>
              <Select
                value={crmStatus}
                onChange={e => setCrmStatus(e.target.value)}
                label="Status *"
                renderValue={s => <StatusBadge status={s} />}
              >
                {CRM_STATUS.map(s => (
                  <MenuItem key={s} value={s}>
                    <StatusBadge status={s} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
              <InputLabel>Coluna</InputLabel>
              <Select
                value={laneId}
                onChange={e => setLaneId(e.target.value)}
                label="Coluna"
                renderValue={id => {
                  const lane = lanes.find(l => l.id === id);
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: "50%",
                        background: lane?.color || "#757575", display: "inline-block"
                      }} />
                      {lane?.title || "Sem coluna"}
                    </div>
                  );
                }}
              >
                {lanes.map(lane => (
                  <MenuItem key={lane.id} value={lane.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: "50%",
                        background: lane.color || "#757575", display: "inline-block"
                      }} />
                      {lane.title}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {crmStatus === "Perdido" && (
              <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
                <InputLabel>Motivo da Perda</InputLabel>
                <Select value={motivoPerda} onChange={e => setMotivoPerda(e.target.value)} label="Motivo da Perda">
                  {MOTIVO_PERDA.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Data de Início"
              type="date"
              value={dataInicio}
              onChange={handleDataInicioChange}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              fullWidth
              style={{ marginBottom: 12 }}
              error={!!dateErrors.dataInicio}
              helperText={dateErrors.dataInicio}
            />

            <TextField
              label="Data Limite"
              type="date"
              value={dataFim}
              onChange={handleDataFimChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dataInicio || today }}
              variant="outlined"
              size="small"
              fullWidth
              style={{ marginBottom: 4 }}
              error={!!dateErrors.dataFim || isExpired}
              helperText={dateErrors.dataFim || (isExpired ? "⚠ Prazo vencido!" : "")}
            />

            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveNegocio}
              disabled={saving}
              style={{ marginTop: 12 }}
            >
              {saving ? <CircularProgress size={16} /> : "Salvar Negócio"}
            </Button>
          </Grid>
        </Grid>

        <Divider style={{ margin: "20px 0" }} />

        {/* ── COMENTÁRIOS ── */}
        <Typography className={classes.sectionTitle}>Comentários</Typography>

        {loadingNotes ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {notes.map(note => (
              <div key={note.id} className={classes.commentBox}>
                <div className={classes.commentHeader}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar style={{ width: 28, height: 28, fontSize: 13 }}>
                      {note.user?.name?.[0] || "?"}
                    </Avatar>
                    <Typography className={classes.commentAuthor}>{note.user?.name}</Typography>
                    <Typography className={classes.commentDate}>
                      {format(parseISO(note.createdAt), "dd/MM/yyyy HH:mm")}
                    </Typography>
                  </div>
                  <div>
                    <Tooltip title="Responder">
                      <IconButton size="small" onClick={() => setReplyingTo(replyingTo === note.id ? null : note.id)}>
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(user.profile === "admin" || note.userId === user.id) && (
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteNote(note.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <Typography className={classes.commentText}>{note.note}</Typography>

                {/* Respostas */}
                {note.replies && note.replies.map(reply => (
                  <div key={reply.id} className={classes.replyBox}>
                    <div className={classes.commentHeader}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Avatar style={{ width: 22, height: 22, fontSize: 11 }}>
                          {reply.user?.name?.[0] || "?"}
                        </Avatar>
                        <Typography className={classes.commentAuthor}>{reply.user?.name}</Typography>
                        <Typography className={classes.commentDate}>
                          {format(parseISO(reply.createdAt), "dd/MM/yyyy HH:mm")}
                        </Typography>
                      </div>
                      {(user.profile === "admin" || reply.userId === user.id) && (
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => handleDeleteNote(reply.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    <Typography className={classes.commentText}>{reply.note}</Typography>
                  </div>
                ))}

                {/* Input de resposta */}
                {replyingTo === note.id && (
                  <div className={classes.replyInput}>
                    <div className={classes.inputRow}>
                      <TextField
                        placeholder="Escreva uma resposta... (mín. 3 caracteres)"
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        className={classes.noteInput}
                        onKeyDown={e => {
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddReply(note.id); }
                        }}
                      />
                      <IconButton color="primary" onClick={() => handleAddReply(note.id)} disabled={!replyText.trim() || replyText.trim().length < 3}>
                        <SendIcon />
                      </IconButton>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Novo comentário */}
            <div className={classes.inputRow}>
              <Avatar style={{ width: 32, height: 32, fontSize: 14 }}>
                {user.name?.[0] || "?"}
              </Avatar>
              <TextField
                placeholder="Adicionar comentário... (mín. 3 caracteres, Enter para enviar)"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={3}
                className={classes.noteInput}
                error={newNote.length > 0 && newNote.trim().length < 3}
                helperText={newNote.length > 0 && newNote.trim().length < 3 ? "Mínimo de 3 caracteres" : ""}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); }
                }}
              />
              <IconButton color="primary" onClick={handleAddNote} disabled={!newNote.trim() || newNote.trim().length < 3}>
                <SendIcon />
              </IconButton>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrmCardModal;
